import { createFileRoute, Link } from "@tanstack/react-router";
import { SOCIAL_PREVIEW_IMAGE, SOCIAL_PREVIEW_ALT } from "@/lib/social-meta";
import { useRef, useState } from "react";
import { LazyTurnstile } from "@/components/site/LazyTurnstile";
import { Flower } from "@/components/site/Flower";
import { PageSection } from "@/components/site/PageSection";
const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string;

const MAX_FILES = 4;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MAX_TOTAL_SIZE = 20 * 1024 * 1024;

export const Route = createFileRoute("/results/share-your-story")({
  head: () => ({
    meta: [
      { title: "Share Your Story | Virginia Bariatric" },
      { name: "robots", content: "noindex, nofollow" },
      {
        name: "description",
        content:
          "Inspire future patients — share your weight-loss journey with Dr. Fitzer and the Virginia Bariatric Surgery team.",
      },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "Share Your Story | Virginia Bariatric" },
        { property: "og:description", content: "Inspire future patients — share your weight-loss journey with Dr. Fitzer and the Virginia Bariatric Surgery team." },
        { property: "og:type", content: "website" },
        { property: "og:url", content: "https://virginiabariatric.com/results/share-your-story" },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: "Share Your Story | Virginia Bariatric" },
        { name: "twitter:description", content: "Inspire future patients — share your weight-loss journey with Dr. Fitzer and the Virginia Bariatric Surgery team." },
        { property: "og:image", content: SOCIAL_PREVIEW_IMAGE },
        { property: "og:image:alt", content: SOCIAL_PREVIEW_ALT },
        { name: "twitter:image", content: SOCIAL_PREVIEW_IMAGE },
        { name: "twitter:image:alt", content: SOCIAL_PREVIEW_ALT },
      ] : []),
    ],
    links: [{ rel: "canonical", href: "https://virginiabariatric.com/results/share-your-story" }],
  }),
  component: ShareYourStoryPage,
});

function ShareYourStoryPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [photos, setPhotos] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    if (files.length > MAX_FILES) {
      setError(`Please select up to ${MAX_FILES} photos.`);
      e.target.value = "";
      return;
    }
    let total = 0;
    for (const f of files) {
      if (f.size > MAX_FILE_SIZE) {
        setError(`"${f.name}" is larger than 5 MB.`);
        e.target.value = "";
        return;
      }
      total += f.size;
    }
    if (total > MAX_TOTAL_SIZE) {
      setError("Total photo size must be under 20 MB.");
      e.target.value = "";
      return;
    }
    setError(null);
    setPhotos(files);
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!turnstileToken) {
      setError("Please complete the security check before sending.");
      return;
    }
    setSending(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("name", name.trim());
      fd.append("email", email.trim());
      fd.append("phone", phone.trim());
      fd.append("message", message.trim());
      fd.append("turnstileToken", turnstileToken);
      for (const file of photos) fd.append("photos", file, file.name);

      const res = await fetch("/api/story-submit", { method: "POST", body: fd });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: "Request failed" }));
        throw new Error(body.error || "Request failed");
      }
      setSent(true);
      setName("");
      setEmail("");
      setPhone("");
      setMessage("");
      setPhotos([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <PageSection className="relative pt-4 md:pt-6 pb-3 md:pb-4">
        <Flower className="hidden md:block right-2 top-6 w-56 lg:w-64" />
        <header className="text-center max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-serif">Share Your Story</h1>
          <p className="mt-3 text-foreground/80 leading-[1.6] text-base md:text-lg">
            Your journey can inspire someone else to take the first step. Tell us about your experience with
            Virginia Bariatric Surgery — and attach before-and-after photos if you'd like.
          </p>
        </header>
      </PageSection>

      <PageSection className="relative z-10 pt-2 pb-4 text-center">
        {sent ? (
          <div className="mx-auto max-w-2xl rounded-2xl border border-border bg-card p-6 shadow-sm text-center">
            <h2 className="text-2xl font-serif">Thank you for sharing your story!</h2>
            <p className="mt-2 text-muted-foreground">
              Your submission has been sent to our team. Here's what happens next:
            </p>
            <ol className="mt-4 text-left text-foreground/80 space-y-2 list-decimal list-inside leading-[1.6]">
              <li>We'll review your story and any photos you shared.</li>
              <li>If we have questions, we'll reach out using the contact info you provided.</li>
              <li>With your permission, your journey may inspire future patients on our Before &amp; After page.</li>
            </ol>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <button
                type="button"
                onClick={() => {
                  setSent(false);
                  setTurnstileToken(null);
                  setError(null);
                }}
                className="rounded-full bg-destructive px-7 py-3 text-sm font-medium text-destructive-foreground hover:opacity-90"
              >
                Share Another Story
              </button>
              <Link
                to="/results"
                className="rounded-full border border-border px-7 py-3 text-sm font-medium text-foreground hover:border-brand hover:text-brand"
              >
                Back to Before &amp; After
              </Link>
              <Link
                to="/"
                className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90"
              >
                Return Home
              </Link>
            </div>
          </div>
        ) : (
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            className="mx-auto max-w-2xl rounded-2xl border border-border bg-card p-4 shadow-sm space-y-3 text-left"
          >
            <label htmlFor="story-name" className="block text-sm">
              <span className="block mb-0.5 font-medium">
                Name<span aria-hidden="true" className="text-brand">*</span>
              </span>
              <input
                id="story-name"
                required
                type="text"
                name="name"
                autoComplete="name"
                maxLength={100}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 outline-none focus:border-brand"
              />
            </label>
            <label htmlFor="story-email" className="block text-sm">
              <span className="block mb-0.5 font-medium">
                Email<span aria-hidden="true" className="text-brand">*</span>
              </span>
              <input
                id="story-email"
                required
                type="email"
                name="email"
                autoComplete="email"
                maxLength={255}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 outline-none focus:border-brand"
              />
            </label>
            <label htmlFor="story-phone" className="block text-sm">
              <span className="block mb-0.5 font-medium">Phone (optional)</span>
              <input
                id="story-phone"
                type="tel"
                name="phone"
                autoComplete="tel"
                maxLength={50}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 outline-none focus:border-brand"
              />
            </label>
            <label htmlFor="story-message" className="block text-sm">
              <span className="block mb-0.5 font-medium">
                Your Story<span aria-hidden="true" className="text-brand">*</span>
              </span>
              <textarea
                id="story-message"
                required
                name="message"
                rows={6}
                maxLength={2000}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 outline-none focus:border-brand resize-y leading-[1.6]"
                placeholder="Share your journey, milestones, and what you'd like others to know..."
              />
            </label>
            <label htmlFor="story-photos" className="block text-sm">
              <span className="block mb-0.5 font-medium">
                Before &amp; After Photos (optional)
              </span>
              <input
                id="story-photos"
                type="file"
                name="photos"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                multiple
                onChange={handleFileChange}
                aria-describedby="story-photos-help"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 outline-none focus:border-brand file:mr-3 file:rounded-md file:border-0 file:bg-brand file:px-3 file:py-1 file:text-sm file:text-white hover:file:opacity-90"
              />
              <span id="story-photos-help" className="mt-1 block text-xs text-muted-foreground">
                Up to {MAX_FILES} photos, 5 MB each (JPEG, PNG, WEBP, or HEIC).
              </span>
              {photos.length > 0 && (
                <ul className="mt-1 text-xs text-muted-foreground list-disc list-inside">
                  {photos.map((f) => (
                    <li key={f.name}>{f.name} ({(f.size / 1024 / 1024).toFixed(1)} MB)</li>
                  ))}
                </ul>
              )}
            </label>
            {error && (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            )}
            <div className="flex justify-center">
              <LazyTurnstile
                siteKey={siteKey}
                triggerRef={formRef}
                onSuccess={(token) => setTurnstileToken(token)}
                onError={() => setError("Security check failed. Please try again.")}
                onExpire={() => setTurnstileToken(null)}
              />
            </div>
            <div className="flex justify-center">
              <button
                type="submit"
                disabled={sending || !turnstileToken}
                className="rounded-full bg-destructive px-7 py-3 text-sm font-medium text-destructive-foreground hover:opacity-90 disabled:opacity-60"
              >
                {sending ? "Sending..." : "Share My Story"}
              </button>
            </div>
          </form>
        )}

        <div className="mt-4 flex flex-wrap justify-center gap-3">
          <Link
            to="/results"
            className="rounded-full border border-border px-7 py-3 text-sm font-medium text-foreground hover:border-brand hover:text-brand"
          >
            Back to Before &amp; After
          </Link>
          <Link
            to="/"
            className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90"
          >
            Back to Homepage
          </Link>
        </div>
      </PageSection>
    </>
  );
}
